import { normalizeTasksReportStatus } from '../tasksReportStatus.ts';
import type { TasksReportStatus } from '../tasksReportStatus.ts';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_COLUMNS = ['description', 'duedate', 'assignee', 'location'];

export type TasksReportColumn =
	| 'description'
	| 'duedate'
	| 'assignee'
	| 'location'
	| 'completedate'
	| 'labels';

type MacroDateParamValue = string | number | undefined;

export interface ParsedMacroParams {
	columns: TasksReportColumn[];
	spaces: string | undefined;
	pages: string | undefined;
	assignees: string | undefined;
	creators: string | undefined;
	labels: string | undefined;
	status: TasksReportStatus;
	duedateFrom: MacroDateParamValue;
	duedateTo: MacroDateParamValue;
	createddateFrom: MacroDateParamValue;
	createddateTo: MacroDateParamValue;
	pageSize: number;
	sortBy: string | undefined;
	reverseSort: boolean;
}

export type RawMacroParams =
	| {
			columns?: { value?: string };
			spaces?: { value?: string };
			pages?: { value?: string };
			assignees?: { value?: string };
			creators?: { value?: string };
			labels?: { value?: string };
			status?: { value?: string };
			duedateFrom?: { value?: string | number };
			duedateTo?: { value?: string | number };
			createddateFrom?: { value?: string | number };
			createddateTo?: { value?: string | number };
			pageSize?: { value?: string };
			sortBy?: { value?: string };
			reverseSort?: { value?: string | boolean };
	  }
	| undefined;

const VALID_COLUMNS: TasksReportColumn[] = [
	'description',
	'duedate',
	'assignee',
	'location',
	'completedate',
	'labels',
];

const VALID_COLUMNS_SET = new Set<TasksReportColumn>(VALID_COLUMNS);

function parseColumns(raw: string | undefined): TasksReportColumn[] {
	if (!raw || typeof raw !== 'string') {
		return DEFAULT_COLUMNS as TasksReportColumn[];
	}

	const parsed: TasksReportColumn[] = [];
	const seen = new Set<TasksReportColumn>();
	const parts = raw.split(',');
	for (const part of parts) {
		const c = part.trim().toLowerCase() as TasksReportColumn;
		if (VALID_COLUMNS_SET.has(c) && !seen.has(c)) {
			seen.add(c);
			parsed.push(c);
		}
	}

	// If no valid columns were found at all, use the documented default set.
	// This covers the case where the user supplied only invalid column names —
	// without this guard, description would be unshifted below and the user
	// would get a single-column table instead of the four-column default.
	if (!parsed.length) {
		return DEFAULT_COLUMNS as TasksReportColumn[];
	}

	// description is always present as the first column.
	if (!parsed.includes('description')) {
		parsed.unshift('description');
	}

	return parsed;
}

export function parseMacroParams(macroParams: RawMacroParams): ParsedMacroParams {
	const raw = macroParams ?? {};
	const columns = parseColumns(raw.columns?.value);
	const pageSize = parseInt(raw.pageSize?.value ?? '', 10) || DEFAULT_PAGE_SIZE;
	const reverseSort = raw.reverseSort?.value === 'true' || raw.reverseSort?.value === true;
	const status = normalizeTasksReportStatus(raw.status?.value);

	return {
		columns,
		spaces: raw.spaces?.value || undefined,
		pages: raw.pages?.value || undefined,
		assignees: raw.assignees?.value || undefined,
		creators: raw.creators?.value || undefined,
		labels: raw.labels?.value || undefined,
		status,
		duedateFrom: raw.duedateFrom?.value ?? undefined,
		duedateTo: raw.duedateTo?.value ?? undefined,
		createddateFrom: raw.createddateFrom?.value ?? undefined,
		createddateTo: raw.createddateTo?.value ?? undefined,
		pageSize,
		sortBy: raw.sortBy?.value || undefined,
		reverseSort,
	};
}
